import { ContactRequestsFilter } from "@/components/common/section-components/filters/ContactRequestsFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { CheckboxCheckedChangeDetails, Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { contactRequestsFilterSchema } from "./schema/contact.schema";
import {
  ContactRequest,
  GetContactRequestListProps,
  GetContactRequestListResponse,
} from "@/services/contact.service";
import { CRHeaderElements } from "./schema/header-elements";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import {
  clearFiltersHandler,
  fetchContactRequestsHandler,
  handleContactConfirmation,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { ContactAction } from "@/components/common/section-components/action/ContactActions";
import { NoData } from "@/components/common/section-components/NoData";
import { usePolling } from "@/hooks/usePolling";
import { useNavigate } from "react-router-dom";

export const ContactPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [contactRequests, setContactRequests] =
    useState<GetContactRequestListResponse | null>({
      listado: [],
      totalPages: 1,
      totalRecords: 10,
    });
  const [loadingContactRequests, setLoadingContactRequests] =
    useState<boolean>(false);
  const [loadingContactConfirmation, setLoadingContactConfirmation] =
    useState<boolean>(false);

  const [selection, setSelection] = useState<string[]>([]);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [currentFilters, setCurrentFilters] =
    useState<GetContactRequestListProps>({
      "P.Pagina": 1,
      "P.Records": 10,
    });

  const { handleSubmit, setValue, getValues, watch, control } = useForm({
    resolver: zodResolver(contactRequestsFilterSchema),
  });

  const fetchContactRequests = async (
    filters?: GetContactRequestListProps,
    showLoad = true,
  ) => {
    await fetchContactRequestsHandler({
      setLoadingContactRequests,
      setContactRequests,
      navigate,
      fetchPermissions,
      showLoad,
      filters,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) fetchContactRequests();
  }, [isActive]);

  usePolling({
    callback: () =>
      fetchContactRequests({ ...currentFilters, "P.Pagina": page }, false),
    delay: 10000,
    isActive,
  });

  const hasSelection = selection?.length > 0;
  const indeterminate =
    hasSelection &&
    selection?.length < Number(contactRequests?.listado?.length);

  const clearFilters = async () => {
    clearFiltersHandler({
      setValue,
      submitFilters,
      getValues,
      setFiltersApplied,
    });
  };

  const { fetchPermissions } = useRoles();
  const navigate = useNavigate();

  const submitFilters = async (event: FieldValues): Promise<void> => {
    submitFiltersHandler({
      setFiltersApplied,
      fetchContactRequests,
      setCurrentFilters,
      setOpenFilters,
      navigate,
      fetchPermissions,
      event,
    });
  };

  const [selectedToContact, setSelectedToContact] = useState<
    ContactRequest | undefined
  >();

  const handleCheck = async (
    _value: CheckboxCheckedChangeDetails,
    item: any,
  ) => {
    handleContactConfirmation({
      currentFilters,
      fetchContactRequests,
      item,
      setLoading: setLoadingContactConfirmation,
      setSelectedToContact,
      navigate,
      fetchPermissions,
    });
  };

  const { userPermissions } = useRoles();

  const handlePageChange = async (page: number) => {
    setPage(page);
    await fetchContactRequests({ ...currentFilters, "P.Pagina": page });
  };

  const watchedValues = watch();
  const isFormEmpty = Object.values(watchedValues).every(
    (val) => val === undefined || val === "",
  );

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.CONTACT_REQUESTS && p.tipoPermiso === "W",
    ),
  );

  const getActionElements = (item: ContactRequest) => (
    <ContactAction
      item={item}
      selectedToContact={selectedToContact}
      loading={loadingContactConfirmation}
      hasPermission={hasWriteAccess}
      onCheck={handleCheck}
    />
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="contact-page"
    >
      <SectionHeader
        showButtons={false}
        title="Solicitudes de contacto"
        loading={loadingContactRequests}
      />

      {/* Filtros */}
      <ContactRequestsFilter
        openFilters={openFilters}
        disableFilters={loadingContactRequests}
        filtersApplied={filtersApplied}
        handleSubmit={handleSubmit}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        disableClear={isFormEmpty}
      />

      <Flex flexDir="column">
        {Boolean(
          !loadingContactRequests && !contactRequests?.listado?.length,
        ) && <NoData />}
        {Boolean(
          !loadingContactRequests && contactRequests?.listado?.length,
        ) && (
          <>
            <StandardTable
              indeterminate={indeterminate}
              items={contactRequests?.listado || []}
              selection={selection}
              setSelection={setSelection}
              headers={CRHeaderElements}
              actionsHeaderLabel="Contacto"
              noSelection
              noActions={!hasWriteAccess}
              actionElements={getActionElements}
            />
            <PaginationComponent
              page={page}
              handlePageChange={handlePageChange}
              pageSize={10}
              count={contactRequests?.totalRecords ?? 0}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
